import clsx from 'clsx';
import { useAtom } from 'jotai';
import Link from 'next/link';
import { useMemo } from 'react';
import { colorManagerAtom } from '../../store/uiStore';
import useMediaSearch from '@/hooks/useMediaSearch';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { FooterLogo } from './Footer';
import AutoSizedText from './AutoSizedText';
import { TPhoto } from '@/interfaces/photos';

const ImageDisplayUniform = ({ currentImages }: { currentImages: TPhoto[] }) => (
  <div className="flex justify-between gap-x-4">
    {currentImages &&
      currentImages?.map((photo) => (
        <Link
          href={`/photo/${photo.id}`}
          key={photo.id}
          className="w-[140px] h-[140px] cursor-pointer"
        >
          {/* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */}
          <img
            src={photo.imgix?.links?.tiny_image_url ?? ''}
            className="w-full h-full object-cover object-center"
            onContextMenu={(e) => e.preventDefault()}
          />
        </Link>
      ))}
  </div>
);

const ImageDisplay = ({ currentImages }: { currentImages: TPhoto[] }) => (
  <div className="flex justify-between items-center gap-x-[calc(10vw-10px)]">
    {currentImages &&
      currentImages?.map((photo) => (
        <Link
          href={`/photo/${photo.id}`}
          key={photo.id}
          className="cursor-pointer"
        >
          <div className={clsx(
            'h-[20vw]',
            photo.orientation === 'landscape' ? 'aspect-[3/2]' : 'aspect-[2/3]'
          )}>
          {/* eslint-disable-next-line @next/next/no-img-element, jsx-a11y/alt-text */}
            <img
              src={photo.imgix?.links?.tiny_image_url ?? ''}
              className={clsx(
                'w-full h-full object-cover object-center'
              )}
              onContextMenu={(e) => e.preventDefault()}
            />
          </div>
        </Link>
      ))}
  </div>
);

const FooterMobile = () => {
  const [colorManager] = useAtom(colorManagerAtom);
  const router = useRouter();

  const { medias: landscapeMedia } = useMediaSearch('', 'Footer search', {
    filters: 'media_type:photo AND orientation:landscape AND published=1',
  });
  const { medias: portraitMedia } = useMediaSearch('', 'Footer search portrait', {
    filters: 'media_type:photo AND orientation:portrait AND published=1',
  });

  const currentImages = useMemo(() => {
    if (!portraitMedia.length && !landscapeMedia.length) {
      return [];
    }

    const selectedPortrait = _.shuffle(portraitMedia);
    const selectedLandscape = _.shuffle(landscapeMedia);

    const result: TPhoto[] = [];
    let idxPortrait = 0;
    let idxLandscape = 0;
    for (let i = 0; i < 6; i++) {
      if (i % 2 === 0) {
        result.push(selectedPortrait[++idxPortrait] || selectedLandscape[++idxLandscape]);
      } else {
        result.push(selectedLandscape[++idxLandscape] || selectedPortrait[++idxPortrait]);
      }
    }
    return result;
  }, [landscapeMedia.length, portraitMedia.length, router.pathname]);

  const currentYear = new Date().getFullYear();

  const allLinks = [
    // Row 1
    { link: '/projects', text: 'Projects' },
    { link: '/moodboard', text: 'Moodboard' },
    { link: 'https://www.instagram.com/deathtostock/', text: 'IG' },
    { link: 'https://twitter.com/deathtostock', text: 'TW' },
    // Row 2
    { link: '/explore', text: 'Explore' },
    { link: '/account', text: 'Account' },
    { link: 'https://www.tiktok.com/@dtstrends', text: 'TK' },
    { link: 'https://open.spotify.com/user/deathtostock?si=laa4F9YiRZSWYtsxd1EPmw', text: 'SPOT' },
    // Row 3
    { link: '/collective', text: 'Creators', width: 2 },
    { link: 'https://www.facebook.com/deathtostockphotos/', text: 'FB', width: 2 },
  ];

  // This is used to break up the email address, which is long enough to break the layout on tiny screens.
  const zws = '\u200B'; // Zero Width Space
  return (
    <div
      className={clsx([
        'border-t pt-16',
        colorManager.text === 'text-black' ? 'border-black' : 'border-white',
      ])}
    >
      <div className="mx-auto px-[20px]">
        <div className="grid gap-y-2 gap-x-4 grid-cols-4 grid-cols-[1fr_2fr_1fr_1fr]">
          <div>
            <div className="w-[64px]">
              <FooterLogo fill={colorManager.text === 'text-black' ? 'black' : 'white'} />
            </div>
          </div>
          <div className="col-span-3 pb-8">
            <p>Say HI</p>
            <a href="mailto:squad@deathtothestockphoto.com" className="">
              squad{zws}@{zws}death{zws}to{zws}the{zws}stock{zws}photo{zws}.com
            </a>
          </div>
          {allLinks.map(({ link, text, width = 1 }, i) => (
            <Link key={link} href={link} className="contents">
              <div style={{ gridColumn: `span ${width} / span ${width}` }}>{text}</div>
            </Link>
          ))}
        </div>

        <div className="pt-16 pb-8">
          <ImageDisplay currentImages={currentImages.slice(0, 3)} />
          <AutoSizedText
            elementType="div"
            continuousSizing={true}
            className="text-center mx-8 my-12 uppercase font-nhg font-normal text-[150px] leading-[1em]"
          >
            <span>Feeling</span>
            <br />
            <span>inspired?</span>
          </AutoSizedText>
          <ImageDisplay currentImages={currentImages.slice(3, 6)} />

          <div className="flex justify-between font-nhg font-normal text-sm leading-none 3xl:text-sm 4xl:text-[15px] pt-16">
            ALL RIGHTS RESERVED
            <div className="w-[2em] inline-block" />© DEATH TO STOCK {currentYear}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FooterMobile;
